/**
 * This file has a sister in the marcom project
 * https://github.com/joylifeinc/marcom/blob/master/src/hooks/useGeoLocale.ts
 */

import { getLocalStorage } from '@shared/core';
import { useRemoteFeatureFlag } from '@shared/core/featureFlags/remoteFeatureFlags';
import { useQueryParamHelper } from '@shared/core/queryString';
import { addError } from '@shared/utils/logger';
import { useCallback, useEffect, useMemo, useState } from 'react';

enum CurrencyCode {
  AUD = 'AUD',
  CAD = 'CAD',
  EUR = 'EUR',
  GBP = 'GBP',
  JPY = 'JPY',
  USD = 'USD'
}

enum CultureCode {
  AU = 'AU',
  CA = 'CA',
  DE = 'DE',
  ES = 'ES',
  FR = 'FR',
  IT = 'IT',
  GB = 'GB',
  JA = 'JA',
  US = 'US'
}

enum LanguageCode {
  de = 'de',
  en = 'en',
  es = 'es',
  fr = 'fr',
  it = 'it',
  ja = 'ja'
}

function findMatchingEnumValue<T>(enumObj: Record<string, T>, key: string): T | undefined {
  const lowerKey = key?.toLowerCase();
  const actualKey = Object.keys(enumObj).find(val => val?.toLowerCase() === lowerKey);
  if (!actualKey) {
    return undefined;
  }

  return enumObj[actualKey];
}

type GeoBasedLocalePayload = Readonly<{
  culture: CultureCode;
  currency: CurrencyCode;
  language: LanguageCode;
}>;

const defaultLocalePayload: GeoBasedLocalePayload = {
  culture: CultureCode.US,
  currency: CurrencyCode.USD,
  language: LanguageCode.en
};

const defaultLocales: ReadonlyArray<GeoBasedLocalePayload> = [
  defaultLocalePayload,
  {
    culture: CultureCode.AU,
    currency: CurrencyCode.AUD,
    language: LanguageCode.en
  },
  {
    culture: CultureCode.CA,
    currency: CurrencyCode.CAD,
    language: LanguageCode.en
  },
  {
    culture: CultureCode.DE,
    currency: CurrencyCode.EUR,
    language: LanguageCode.de
  },
  {
    culture: CultureCode.ES,
    currency: CurrencyCode.EUR,
    language: LanguageCode.es
  },
  {
    culture: CultureCode.FR,
    currency: CurrencyCode.EUR,
    language: LanguageCode.fr
  },
  {
    culture: CultureCode.IT,
    currency: CurrencyCode.EUR,
    language: LanguageCode.it
  },
  {
    culture: CultureCode.US,
    currency: CurrencyCode.EUR,
    language: LanguageCode.es
  },
  {
    culture: CultureCode.GB,
    currency: CurrencyCode.GBP,
    language: LanguageCode.en
  },
  {
    culture: CultureCode.JA,
    currency: CurrencyCode.JPY,
    language: LanguageCode.ja
  }
];

const findClosestMatchingLocale = (payload?: Partial<{ culture: string; currency: string; language: string }>): GeoBasedLocalePayload | undefined => {
  if (!payload) {
    return undefined;
  }

  let culture = findMatchingEnumValue(CultureCode, payload.culture as string);
  let currency = findMatchingEnumValue(CurrencyCode, payload.currency as string);
  let language = findMatchingEnumValue(LanguageCode, payload.language as string);

  // handle extended language codes (eg. en-AU, es-US)
  if (!language && !culture && payload.language?.includes('-')) {
    const split = payload.language.split('-', 2);
    language = findMatchingEnumValue(LanguageCode, split[0]);
    culture = findMatchingEnumValue(CultureCode, split[1]);
  }

  // if any properties are missing, fill them in from the default locales
  if (!culture || !currency || !language) {
    defaultLocales.forEach(defaultLocale => {
      if (defaultLocale.culture === culture || defaultLocale.currency === currency || defaultLocale.language === language) {
        culture = culture || defaultLocale.culture;
        currency = currency || defaultLocale.currency;
        language = language || defaultLocale.language;
      }
    });
  }

  if (culture && currency && language) {
    return {
      culture,
      currency,
      language
    };
  }

  return undefined;
};

const constructLanguageCodeIso15897 = (language: LanguageCode, culture: CultureCode): string => {
  return `${language}-${culture}`;
};

const getLocallyStoredGeoBasedLocale = (): {
  value: GeoBasedLocalePayload | undefined;
  setValue: (locale: GeoBasedLocalePayload) => void;
} => {
  const localStorage = getLocalStorage();
  const storedLocale = localStorage.getItem('joyLocale');

  const setValue = (updatedLocale: GeoBasedLocalePayload) => {
    const locale = findClosestMatchingLocale(updatedLocale);
    if (locale) {
      localStorage.setItem('joyLocale', JSON.stringify(locale));
    }
  };

  if (!storedLocale) {
    return { value: undefined, setValue };
  }

  try {
    const locale = findClosestMatchingLocale(JSON.parse(storedLocale));
    return { value: locale, setValue };
  } catch (error) {
    addError(error as Error, {});
  }
  return { value: undefined, setValue };
};

const useSearchParamsGeoLocale = (): Partial<GeoBasedLocalePayload> | undefined => {
  const { getValueString } = useQueryParamHelper();

  const culture = getValueString('culture') || getValueString('cul');
  const currency = getValueString('currency') || getValueString('cur');
  const language = getValueString('language') || getValueString('lng') || getValueString('lang') || getValueString('l');

  return findClosestMatchingLocale({ culture, currency, language });
};

const useRemoteGeoBasedLocale = ({ skip }: { skip: boolean }): { loading: boolean; value: GeoBasedLocalePayload | undefined } => {
  const { payload, loading } = useRemoteFeatureFlag('geoBasedLocale', {
    skip
  });
  const locale = payload && typeof payload === 'object' ? findClosestMatchingLocale(payload) : undefined;
  return { loading, value: locale };
};

const addLanguageCodeIso15897 = (locale: GeoBasedLocalePayload): GeoBasedLocaleWithIso15897 => {
  return {
    ...locale,
    languageCodeIso15897: constructLanguageCodeIso15897(locale.language, locale.culture)
  };
};

type GeoBasedLocaleWithIso15897 = GeoBasedLocalePayload & {
  languageCodeIso15897: string;
};

type GeoBasedLocaleHookReturn = Readonly<{
  loading: boolean;
}> &
  Partial<GeoBasedLocaleWithIso15897>;

/**
 * Hook to determine the user's locale based on search parameters, local storage, or remote fetch.
 * It will first check the search parameters for locale information, then local storage, and finally remote fetch to amplitude.
 *
 * For variants see https://app.amplitude.com/experiment/joylife/142658/config/451997/configure
 * @param defaultValues Default values to use if no locale is found in search params, local storage, or remote fetch.
 * If not provided, it will default to the US locale.
 * @returns An object containing the locale information, including culture, currency, language, and languageCodeIso15897.
 * The `loading` property indicates whether the locale is still being determined.
 * If no locale is found, it will return the default values.
 */
export const useGeoBasedLocale = (defaultValues?: GeoBasedLocalePayload): GeoBasedLocaleHookReturn => {
  const searchParams = useSearchParamsGeoLocale();
  const { value: localValues, setValue: setLocalValues } = getLocallyStoredGeoBasedLocale();
  // if we have search params or local values, we skip the remote fetch
  const { value: remoteValues, loading: remoteLocaleLoading } = useRemoteGeoBasedLocale({ skip: !!searchParams || !!localValues });

  const [languageCode, setLanguageCode] = useState<GeoBasedLocaleWithIso15897 | undefined>();

  const setLanguageCodeAndSync = useCallback(
    (locale: GeoBasedLocalePayload): void => {
      setLanguageCode(addLanguageCodeIso15897(locale));
      setLocalValues(locale);
    },
    [setLocalValues]
  );

  useEffect(() => {
    if (languageCode) {
      // languageCode is already set, no need to change it
      return;
    }

    const searchParamLocale = findClosestMatchingLocale(searchParams);
    if (searchParamLocale) {
      setLanguageCodeAndSync(searchParamLocale);
      return;
    }

    const localStorageLocale = findClosestMatchingLocale(localValues);
    if (localStorageLocale) {
      setLanguageCodeAndSync(localStorageLocale);
      return;
    }

    const remoteLocale = findClosestMatchingLocale(remoteValues);
    if (remoteLocale) {
      setLanguageCodeAndSync(remoteLocale);
      return;
    }

    if (remoteLocaleLoading) {
      return;
    }

    // fallback to default values
    setLanguageCodeAndSync(defaultValues ?? defaultLocalePayload);
  }, [defaultValues, languageCode, localValues, remoteValues, searchParams, setLocalValues, remoteLocaleLoading, setLanguageCodeAndSync]);

  return useMemo<GeoBasedLocaleHookReturn>(
    () => ({
      loading: !languageCode,
      ...languageCode
    }),
    [languageCode]
  );
};
